import { createContext, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useUserData } from "./userContext";
import { stat_getStat2Weighted } from "src/data/stat/statFunctions";
import { asiakas_asiakasLinks_fetch, asiakas_fetch } from "src/data/asiakas/AsiakasFunctions";

export const GridKeikka_NewKeikkaContext = createContext();

export const GridKeikka_NewKeikkaProvider = ({ children, defaults }) => {
  const { user, userAsiakas, token } = useUserData();

  const [ownerAsiakasId, setOwnerAsiakasId] = useState(null);
  const [sourceAsiakas, setSourceAsiakas] = useState(null);
  const [sourceAsiakasList, setSourceAsiakasList] = useState([]);
  const [sourceAsiakasListLoaded, setSourceAsiakasListLoaded] = useState(false);
  const [betoniAsiakas, setBetoniAsiakas] = useState(null);
  const [betoniAsiakasList, setBetoniAsiakasList] = useState([]);
  const [pumppuAsiakas, setPumppuAsiakas] = useState(null);
  const [pumppuAsiakasList, setPumppuAsiakasList] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isBetoniSuoraLaskutus, setIsBetoniSuoraLaskutus] = useState(false);
  const [isPumppuSuoraLaskutus, setIsPumppuSuoraLaskutus] = useState(true);
  const [sourceAsiakasSettings, setSourceAsiakasSettings] = useState([]);
  const [isKaanteinenLaskutus, setIsKaanteinenLaskutus] = useState(false);
  const [accordionExpanded, setAccordtionExpanded] = useState(false);

  useEffect(() => {
    if (!userAsiakas) return;
    if (userAsiakas?.asiakasId !== ownerAsiakasId) setOwnerAsiakasId(userAsiakas.asiakasId);
    const _asynk = async () => {
      const _sourceAsiakasList = await generateSourceAsiakasList(
        userAsiakas,
        token,
        setSourceAsiakasSettings
      );
      setSourceAsiakasList(_sourceAsiakasList);
      setSourceAsiakasListLoaded(true);
    };
    _asynk();
  }, [userAsiakas]);

  useEffect(() => {
    if (!userAsiakas) return;
    if (!ownerAsiakasId) return;

    const asynk = async () => {
      setOwnerAsiakasId(userAsiakas?.asiakasId);

      const result = await stat_getStat2Weighted(ownerAsiakasId, token, true);
      // Initialize objects to store the aggregated sums
      const betoniAsiakasSum = {};
      const pumppuAsiakasSum = {};
      const sourceAsiakasSum = {};

      if (result === false) {
        setIsError(true);
        return;
      }

      // Aggregate the data
      for (const item of result) {
        // Aggregate by betoniAsiakasId
        if (!betoniAsiakasSum[item.betoniAsiakasId]) {
          betoniAsiakasSum[item.betoniAsiakasId] = {
            betoniAsiakasId: item.betoniAsiakasId,
            betoniAsiakasNimi: item.betoniAsiakasNimi,
            weightedM3: 0,
          };
        }
        betoniAsiakasSum[item.betoniAsiakasId].weightedM3 += item.weightedM3;

        // Aggregate by pumppuAsiakasId
        if (!pumppuAsiakasSum[item.pumppuAsiakasId]) {
          pumppuAsiakasSum[item.pumppuAsiakasId] = {
            pumppuAsiakasId: item.pumppuAsiakasId,
            pumppuAsiakasNimi: item.pumppuAsiakasNimi,
            weightedM3: 0,
          };
        }
        pumppuAsiakasSum[item.pumppuAsiakasId].weightedM3 += item.weightedM3;

        // Aggregate by sourceAsiakasId
        if (!sourceAsiakasSum[item.sourceAsiakasId]) {
          sourceAsiakasSum[item.sourceAsiakasId] = {
            sourceAsiakasId: item.sourceAsiakasId,
            sourceAsiakasNimi: item.sourceAsiakasNimi,
            weightedM3: 0,
            sourceAsiakasColorCode: item.sourceAsiakasColorCode,
          };
        }
        sourceAsiakasSum[item.sourceAsiakasId].weightedM3 += item.weightedM3;
      }

      // Convert the aggregated objects to arrays
      const betoniList = Object.values(betoniAsiakasSum);
      const pumppuList = Object.values(pumppuAsiakasSum);
      const sourceList = Object.values(sourceAsiakasSum);

      // Sort the arrays by the weightedM3
      betoniList.sort((a, b) => b.weightedM3 - a.weightedM3);
      pumppuList.sort((a, b) => b.weightedM3 - a.weightedM3);

      // Set the state
      setBetoniAsiakasList(betoniList);
      setPumppuAsiakasList(pumppuList);

      // Set selected values
      // set defaults
      // set sourceAsiakas
      if (defaults?.sourceAsiakasId)
        setSourceAsiakas(
          sourceList.find((item) => item.sourceAsiakasId === defaults.sourceAsiakasId)
        );
      else
        setSourceAsiakas(sourceList.find((item) => item.sourceAsiakasId === userAsiakas.asiakasId));
      // set betoniAsiakas
      if (defaults?.betoniAsiakasId)
        setBetoniAsiakas(
          betoniList.find((item) => item.betoniAsiakasId === defaults.betoniAsiakasId)
        );
      else if (userAsiakas?.isBetonitoimittaja)
        setBetoniAsiakas(sourceList.find((item) => item.sourceAsiakasId === userAsiakas.asiakasId));
      else setBetoniAsiakas(betoniList[0]);
      // set pumppuAsiakas
      if (defaults?.pumppuAsiakasId)
        setPumppuAsiakas(
          pumppuList.find((item) => item.pumppuAsiakasId === defaults.pumppuAsiakasId)
        );
      else if (userAsiakas?.isPumppuToimittaja)
        setPumppuAsiakas(sourceList.find((item) => item.sourceAsiakasId === userAsiakas.asiakasId));
      else setPumppuAsiakas(pumppuList[0]);

      // find userAsiakas.asiakasId from sourceAsiakasList
      const sourceAsiakas = sourceList.find(
        (item) => item.sourceAsiakasId === userAsiakas.asiakasId
      );
      setSourceAsiakas(sourceAsiakas);

      // finalize
      setIsLoaded(true);
      setIsError(false);
    };
    asynk();
  }, [ownerAsiakasId]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!sourceAsiakas) return;
    const asynk = async () => {
      // get row from sourceAsiakasSettings where linkedAsiakasId === sourceAsiakas.sourceAsiakasId
      const row = sourceAsiakasSettings.find(
        (item) => item.linkedAsiakasId === sourceAsiakas.sourceAsiakasId
      );
      if (!row) return;
      const j = JSON.parse(row.asiakasAsiakasLinkJSON);
      // suoralaskutuksen (obsolete)
      if (j.isBetoniSuoraLaskutus !== isBetoniSuoraLaskutus)
        setIsBetoniSuoraLaskutus(j.isBetoniSuoraLaskutus);
      if (j.isPumppuSuoraLaskutus !== isPumppuSuoraLaskutus)
        setIsPumppuSuoraLaskutus(j.isPumppuSuoraLaskutus);
      if (j.kaanteinenLaskutus !== isKaanteinenLaskutus)
        setIsKaanteinenLaskutus(j.kaanteinenLaskutus);
      // Set betoni asiakas (obsolete)
      if (j.setBetoniAsiakas) {
        const found = betoniAsiakasList.find(
          (asiakas) => asiakas.betoniAsiakasId === sourceAsiakas.sourceAsiakasId
        );
        if (found && betoniAsiakas.betoniAsiakasId !== found.betoniAsiakasId)
          setBetoniAsiakas(found);
      }
      // Set pumppu asiakas (obsolete)
      if (j.setPumppuAsiakas) {
        const found = pumppuAsiakasList.find(
          (asiakas) => asiakas.pumppuAsiakasId === sourceAsiakas.sourceAsiakasId
        );
        if (found && pumppuAsiakas.pumppuAsiakasId !== found.pumppuAsiakasId)
          setPumppuAsiakas(found);
      }
      // Default betoni asiakas id
      if (j.defaultBetoniAsiakasId > 0) {
        let found = betoniAsiakasList.find(
          (asiakas) => asiakas.betoniAsiakasId === j.defaultBetoniAsiakasId
        );
        if (!found) {
          const _asiakas = await asiakas_fetch(user, j.defaultBetoniAsiakasId, token);
          console.log(_asiakas.globalAsiakas);
          const newAsiakas = {
            betoniAsiakasId: _asiakas.globalAsiakas.asiakasId,
            betoniAsiakasNimi: _asiakas.globalAsiakas.asiakasNimi,
            weightedM3: 0,
          };
          setBetoniAsiakasList([...betoniAsiakasList, newAsiakas]);
          found = newAsiakas;
        }
        setBetoniAsiakas(found);
      }
      // Default pumppu asiakas id
      if (j.defaultPumppuAsiakasId > 0) {
        let found = pumppuAsiakasList.find(
          (asiakas) => asiakas.pumppuAsiakasId === j.defaultPumppuAsiakasId
        );
        if (!found) {
          const _asiakas = await asiakas_fetch(user, j.defaultPumppuAsiakasId, token);
          const newAsiakas = {
            pumppuAsiakasId: _asiakas.globalAsiakas.asiakasId,
            pumppuAsiakasNimi: _asiakas.globalAsiakas.asiakasNimi,
            weightedM3: 0,
          };
          setPumppuAsiakasList([...pumppuAsiakasList, newAsiakas]);
          found = newAsiakas;
        }
        setPumppuAsiakas(found);
      }
    };
    asynk();
  }, [sourceAsiakas, isLoaded, sourceAsiakasSettings]);

  const doSetSourceAsiakas = (asiakas) => {
    setSourceAsiakas(asiakas);
  };

  const reload = async () => {
    const _sourceAsiakasList = await generateSourceAsiakasList(
      userAsiakas,
      token,
      setSourceAsiakasSettings
    );
    setSourceAsiakasList(_sourceAsiakasList);
  };

  return (
    <GridKeikka_NewKeikkaContext.Provider
      value={{
        ownerAsiakasId,
        setOwnerAsiakasId,
        sourceAsiakas,
        setSourceAsiakas: doSetSourceAsiakas,
        sourceAsiakasList,
        setSourceAsiakasList,
        sourceAsiakasListLoaded,
        betoniAsiakas,
        setBetoniAsiakas,
        betoniAsiakasList,
        setBetoniAsiakasList,
        pumppuAsiakas,
        setPumppuAsiakas,
        pumppuAsiakasList,
        setPumppuAsiakasList,
        isLoaded,
        isError,
        isBetoniSuoraLaskutus,
        setIsBetoniSuoraLaskutus,
        isPumppuSuoraLaskutus,
        setIsPumppuSuoraLaskutus,
        sourceAsiakasSettings,
        setSourceAsiakasSettings,
        reload,
        accordionExpanded,
        setAccordtionExpanded,
      }}>
      {children}
    </GridKeikka_NewKeikkaContext.Provider>
  );
};

GridKeikka_NewKeikkaProvider.propTypes = {
  children: PropTypes.node.isRequired,
  defaults: PropTypes.object,
};

export const useGridKeikka_NewKeikkaData = () => useContext(GridKeikka_NewKeikkaContext);
async function generateSourceAsiakasList(userAsiakas, token, setSourceAsiakasSettings) {
  const a = await asiakas_asiakasLinks_fetch(userAsiakas.asiakasId, 1, token);
  setSourceAsiakasSettings(a);
  const _sourceAsiakasList = [];
  for (const item of a) {
    _sourceAsiakasList.push({
      sourceAsiakasId: item.linkedAsiakasId,
      sourceAsiakasNimi: item.linkedAsiakasNimi,
      sourceAsiakasColorCode: item.linkedAsiakasColorCode,
    });
  }
  return _sourceAsiakasList;
}
